import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { googleLogout } from "@react-oauth/google";
import axios from "axios";
import { Roles } from "./Roles";
import { message } from "antd";
import { useAuthHeartbeat } from "hooks/useAuthHeartbeat";
import { attachAuthInterceptor } from "components/utils/axiosInstance";

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Pure helpers — defined outside the component for stable references
// ---------------------------------------------------------------------------

/**
 * Derives the Roles constant from API numeric fields.
 */
const resolveRole = (usersTypeId, parent) => {
  switch (parseInt(usersTypeId, 10)) {
    case 2:  return parseInt(parent, 10) > 0 ? Roles.TDR : Roles.TMM;
    case 3:  return Roles.CLIENT;
    case 4:  return Roles.CLIENTMANAGER;
    case 5:  return Roles.AGENTMANAGER;
    case 6:  return Roles.PARTNER;
    case 7:  return Roles.RIDER;
    case 8:  return Roles.FULFILMENTPROVIDER;
    default: return Roles.ADMIN;
  }
};

/**
 * Builds a fully-populated user object from the raw API response.
 * All fields the app needs live here — no scattered localStorage reads.
 */
const buildUserData = (apiData, picture = null) => {
  const {
    Id,
    Name,
    FulfillmentCenterId,
    UsersTypeId,
    Parent,
    UserCode,
    CompanyId,
    CampaignCode,
    BrandCampaignId,
    AccessToken,
  } = apiData;

  return {
    id:                 parseInt(Id, 10),
    name:               Name,
    picture:            picture ?? null,
    role:               resolveRole(UsersTypeId, Parent),
    userCode:           UserCode       ?? null,
    accessToken:        AccessToken    ?? null,
    fulfillmentCenterId: FulfillmentCenterId ? parseInt(FulfillmentCenterId, 10) : null,
    campaignCode:       CampaignCode   ?? null,
    brandCampaignId:    BrandCampaignId ? parseInt(BrandCampaignId, 10) : null,
    companyId:          CompanyId      ? parseInt(CompanyId, 10)       : null,
    parentId:           Parent         ? parseInt(Parent, 10)          : null,
    usersTypeId:        UsersTypeId    ? parseInt(UsersTypeId, 10)     : null,
  };
};

/**
 * Writes the user object and session expiry to localStorage.
 * Single JSON blob + a few individual keys kept for backward-compat
 * (e.g. axios interceptors that read accessToken directly).
 */
const persistSession = (userData, expiryMs) => {
  localStorage.setItem("user",               JSON.stringify(userData));
  localStorage.setItem("sessionExpiry",      String(expiryMs));
  // Legacy individual keys — migrate consumers to getUserInfo() over time
  localStorage.setItem("accessToken",        userData.accessToken       ?? "");
  localStorage.setItem("userRole",           userData.role              ?? "");
  localStorage.setItem("id",                 String(userData.id));
  localStorage.setItem("UserCode",           userData.userCode          ?? "");
  localStorage.setItem("fulfillmentCenterId",String(userData.fulfillmentCenterId));
  localStorage.setItem("CampaignCode",       userData.campaignCode      ?? "");
  localStorage.setItem("brandCampaignId",    String(userData.brandCampaignId));
  localStorage.setItem("companyId",          String(userData.companyId));
  localStorage.setItem("usersTypeId",        String(userData.usersTypeId));
};

const clearSession = () => localStorage.clear();

const safeParseUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) ?? null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user,          setUser]          = useState(null);
  const [isLoggedIn,    setIsLoggedIn]    = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // ------------------------------------------------------------------
  // Restore session on mount (runs once)
  // ------------------------------------------------------------------
  useEffect(() => {
    const expiry = parseInt(localStorage.getItem("sessionExpiry"), 10);
    const now    = Date.now();

    if (expiry && now < expiry) {
      const stored = safeParseUser();
      if (stored?.id) {
        setUser(stored);
        setIsLoggedIn(true);
      }
    } else {
      clearSession();
    }

    setIsAuthLoading(false);
  }, []);

  // ------------------------------------------------------------------
  // Logout
  // ------------------------------------------------------------------
  const handleLogout = useCallback(() => {
    googleLogout();
    clearSession();
    setUser(null);
    setIsLoggedIn(false);
    navigate("/login", { replace: true });
  }, [navigate]);

  // ------------------------------------------------------------------
  // Google Login
  // ------------------------------------------------------------------
  const loginWithGoogle = async (googleAccessToken) => {
    try {
      const { data: profile } = await axios.get(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${googleAccessToken}`,
        {
          headers: {
            Authorization: `Bearer ${googleAccessToken}`,
            Accept: "application/json",
          },
        },
      );

      const { data: apiData } = await axios.post(
        `${process.env.REACT_APP_TRADEPLUS_URL}/api/Auth`,
        { email: profile.email },
      );

      if (parseInt(apiData.IsValid, 10) !== 1) {
        message.error("Access denied: You are not allowed to log in");
        return false;
      }

      const userData = buildUserData(apiData, profile.picture ?? null);
      const expiry   = Date.now() + 6 * 60 * 60 * 1000; // 6 h

      persistSession(userData, expiry);
      setUser(userData);
      setIsLoggedIn(true);

      return userData;
    } catch (error) {
      message.error("Login failed. Contact Admin.");
      console.error("Google login failed:", error);
      return false;
    }
  };

  // ------------------------------------------------------------------
  // Email / Password Login
  // ------------------------------------------------------------------
  const loginWithEmail = async (email, password, remember = false) => {
    try {
      const { data: apiData } = await axios.post(
        `${process.env.REACT_APP_TRADEPLUS_URL}/api/Auth`,
        { email, password },
      );

      if (parseInt(apiData.IsValid, 10) !== 1) {
        message.error("Access denied: You are not allowed to log in");
        return false;
      }

      const userData = buildUserData(apiData, null);
      const expiry   = remember
        ? Date.now() + 7 * 24 * 60 * 60 * 1000  // 7 days
        : Date.now() + 6 * 60 * 60 * 1000;       // 6 h

      persistSession(userData, expiry);
      setUser(userData);
      setIsLoggedIn(true);

      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        message.error("Invalid email or password");
      } else {
        message.error("Login failed. Please try again later.");
      }
      console.error("Email login failed:", error);
      return false;
    }
  };

  // ------------------------------------------------------------------
  // getUserInfo — reads from React state, not raw localStorage calls.
  // Falls back to localStorage only during the brief hydration window.
  // ------------------------------------------------------------------
  const getUserInfo = useCallback(() => {
    const src = user ?? safeParseUser();
    return {
      id:                  src?.id                  ?? null,
      name:                src?.name                ?? "User",
      picture:             src?.picture             ?? null,
      role:                src?.role                ?? null,
      userCode:            src?.userCode            ?? null,
      // Kept as capitalised aliases so call-sites don't need to change
      UserCode:            src?.userCode            ?? null,
      accessToken:         src?.accessToken         ?? null,
      token:               src?.accessToken         ?? null,
      fulfillmentCenterId: src?.fulfillmentCenterId ?? null,
      CampaignCode:        src?.campaignCode        ?? null,
      campaignCode:        src?.campaignCode        ?? null,
      brandCampaignId:     src?.brandCampaignId     ?? null,
      companyId:           src?.companyId           ?? null,
      usersTypeId:         src?.usersTypeId         ?? null,
    };
  }, [user]);

  // ------------------------------------------------------------------
  // Heartbeat + axios interceptor
  // ------------------------------------------------------------------
  useAuthHeartbeat({
    isLoggedIn,
    token: user?.accessToken ?? null,
    logout: handleLogout,
  });

  useEffect(() => {
    attachAuthInterceptor(handleLogout);
  }, [handleLogout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        isAuthLoading,
        setUser,
        setIsLoggedIn,
        logout:          handleLogout,
        getUserInfo,
        loginWithGoogle,
        loginWithEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};